import { MenuItem, TextField } from '@mui/material'
import { useDiscountRates } from '@/api/hooks'

/**
 * A discount, chosen off the list the business gives, and only off it.
 *
 * <p>The same job used to be given three per cent at one desk and ten at
 * another because the box was empty and everybody guessed. The list is now the
 * answer rather than a suggestion, and the server refuses anything else, so
 * there is nothing to type here at all.</p>
 *
 * <p>A job sold at a figure that is not on the list - agreed before the list
 * was written - keeps it, shown as it stands. Changing it means choosing a
 * listed rate, and what is not offered cannot be chosen again by accident.</p>
 */
export function DiscountField({
  value,
  onChange,
  label = 'Discount %',
  helperText,
  disabled,
  onReason,
}: {
  value: string
  onChange: (percent: string) => void
  label?: string
  helperText?: string
  disabled?: boolean
  /** Told what the rate is given for, where the caller keeps a reason. */
  onReason?: (reason: string) => void
}) {
  const { data: rates } = useDiscountRates()
  const offered = rates ?? []
  // A figure agreed before the list was written stays choosable while it is
  // showing, so opening an old order does not silently change what it was sold
  // at. It disappears from the list the moment anything else is chosen.
  const agreedButNotListed =
    Boolean(value) && Number(value) > 0
    && !offered.some((rate) => String(rate.percent) === value)

  return (
    <TextField
      select
      fullWidth
      label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => {
        onChange(event.target.value)
        const chosen = offered.find((rate) => String(rate.percent) === event.target.value)
        if (chosen?.label) onReason?.(chosen.label)
      }}
      helperText={helperText}
    >
      <MenuItem value="">No discount</MenuItem>
      {offered.map((rate) => (
        <MenuItem key={rate.id} value={String(rate.percent)}>
          {rate.percent}%{rate.label ? ` · ${rate.label}` : ''}
        </MenuItem>
      ))}
      {agreedButNotListed ? (
        <MenuItem value={value}>{value}% · agreed before this list</MenuItem>
      ) : null}
    </TextField>
  )
}
